import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, Controller, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  Report2CreateFormSchemas,
  Report2CSVDataSchemas,
  Report2CreateFormTypes,
  Report2CSVDataTypes,
} from 'schemas/reports-#-2-schemas'

import {
  useReportMerchantsMutation,
  useReportMerchantsSelectQuery,
} from './use-report-merchants-lookup'
import {
  TRANSACTION_REPORT_INCLUDE_TRADER,
  TRANSACTION_REPORT_CALENDAR_UNIT,
  TRANSACTION_REPORT_SHOWSUBGROUP,
  TRANSACTION_REPORT_SHOWGRANDTOTAL,
  TRANSACTION_REPORT_TYPE,
  TRANSACTION_REPORT_DIMCOLUMNORDER,
} from 'constants/filters'
import { ROUTES } from 'constants/routing'
import { useReport2Mutation } from './use-report-#-2'
import { ExportToCSV } from '../../utils-reports-actions'

export const CreateReport2Form = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const [queryString, queryStringSet] = useState<string | null>(null)
  const { onSearchMerchantsLoader } = useReportMerchantsMutation()
  const { selectedMerchants, onSelectMerchants } =
    useReportMerchantsSelectQuery()
  const [isAllTraders, isAllTradersSet] = useState(false)

  const traderIds: number[] = [] // this will be overwritten with merchant lookup trader ids
  const date = new Date().toISOString().split('T')[0]
  const formDefaultValues: Report2CreateFormTypes = {
    includeAllTrader: 1,
    fromTraderIds: traderIds,
    reportTypeId: '1',
    startDate: date,
    endDate: date,
    calendarUnit: 4,
    showSubGroupTotal: 0,
    showGrandTotal: 1,
    dimColumnOrder: [1],
  }

  const checkboxOptions = [
    { label: "Trader ID/Name", value: 1 },
    { label: "Transaction Type", value: 2 },
    { label: "Trader Type", value: 3 },
    { label: "Calendar Unit", value: 4 },
  ];

  const form = useForm<Report2CreateFormTypes>({
    resolver: zodResolver(Report2CreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const dimColumnOrder = watch("dimColumnOrder");

  const {
    data: report2Data,
    mutateAsync: postReport2Mutation,
    isSuccess,
    isError,
    isLoading,
  } = useReport2Mutation()

  const handleView = useCallback(() => {
    navigate(
      ROUTES.ReportsView.path +
      `/?report-query=${queryString}&report-type=report_2`
    )
  }, [queryString, navigate])

  const onSubmit = useCallback(
    async (formData: Report2CreateFormTypes) => {
      const queryString = encodeURIComponent(JSON.stringify(formData))
      queryStringSet(queryString)
      postReport2Mutation(formData)
    },
    [postReport2Mutation, queryStringSet]
  )

  useEffect(() => {
    form.setValue(
      'fromTraderIds',
      selectedMerchants.map((item) => Number(item.value))
    )
  }, [form, selectedMerchants])

  useEffect(() => {
    if (resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isSuccess, isError, resultRef])

  useEffect(() => {
    isAllTradersSet(form.watch('includeAllTrader') === 1)
  }, [form.watch('includeAllTrader')])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          control={form.control}
          name="includeAllTrader"
          render={({ field: { onChange, onBlur, value } }) => (
            <Dropdown
              defaultValue={TRANSACTION_REPORT_INCLUDE_TRADER.find(
                (i) => i.value === String(value)
              )}
              title="Merchant Selection"
              options={TRANSACTION_REPORT_INCLUDE_TRADER}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
                form.setValue(
                  'fromTraderIds',
                  option.value === '1' ? null : traderIds
                )
                isAllTradersSet(option.value === '1' ? true : false)
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {!isAllTraders ? (
        <div className="mb-4">
          <Controller
            name="fromTraderIds"
            control={form.control}
            render={({ field }) => (
              <Dropdown
                title="Lookup merchants"
                loadOptions={onSearchMerchantsLoader}
                onChange={(option: DropDownItem) => {
                  onSelectMerchants(option)
                }}
                // onInputChange={(value) => onSearchMerchants(value)}
                className="w-full tracking-wider"
                borderColor="#C4C4C4"
                containerClassName="focus:border-primary"
                placeholder="Search by Merchant Name..."
                isClearable
                isSearchable
                async
              />
            )}
          />
        </div>
      ) : null}

      {!isAllTraders && selectedMerchants.length ? (
        <div className="mb-4">
          <p className="font-light tracking-wide">Selected Merchants</p>
          <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
            {selectedMerchants.map((item) => (
              <span
                onClick={() => onSelectMerchants(item)}
                key={item.value}
                className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
              >
                {item.label}
                <CloseIcon className="w-3 h-3" />
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="reportTypeId"
          render={({ field: { onChange, onBlur, value } }) => (
            <Dropdown
              title="Filter"
              options={TRANSACTION_REPORT_TYPE}
              onSelect={(option: DropDownItem) => {
                onChange(option.value)
              }}
              className="w-full"
            />
          )}
        />
      </div>


      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.startDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.endDate?.message}
            />
          )}
        />
      </div>

      {/* <div className="mb-4">
        <Controller
          control={form.control}
          name="showSubGroupTotal"
          render={({ field: { onChange, onBlur, value } }) => (
            <Dropdown
              defaultValue={TRANSACTION_REPORT_SHOWSUBGROUP.find(
                (i) => i.value === String(value)
              )}
              title="Show/Hide Sub-Group Total"
              options={TRANSACTION_REPORT_SHOWSUBGROUP}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="showGrandTotal"
          render={({ field: { onChange, onBlur, value } }) => (
            <Dropdown
              defaultValue={TRANSACTION_REPORT_SHOWGRANDTOTAL.find(
                (i) => i.value === String(value)
              )}
              title="Show/Hide Grand Total"
              options={TRANSACTION_REPORT_SHOWGRANDTOTAL}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div> */}

      <div className="mb-4">
        <span className="tracking-wider font-light">Columns to include</span>
        <div className="flex items-center mb-4 mt-2 flex-wrap gap-4">
          {checkboxOptions.map(({ label, value }) => {
            const id = `dimCol-${value}`;
            const isChecked = dimColumnOrder.includes(value);

            return (
              <div key={value} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={id}
                  checked={isChecked}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...dimColumnOrder, value]
                      : dimColumnOrder.filter((v: number) => v !== value);

                    setValue('dimColumnOrder', updated, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
                <label htmlFor={id} className="text-gray-500 font-light">
                  {label}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {errors.dimColumnOrder && (
        <p className="text-red font-light tracking-wide">
          {errors.dimColumnOrder.message}
        </p>
      )}

      <div>
        <p className="tracking-wider font-light mb-1">Selected Dimensions:</p>
        <ul className="list-disc pl-6 text-sm text-gray-800">
          {dimColumnOrder.map((value) => {
            const option = checkboxOptions.find((opt) => opt.value === value)
            return <li key={value}>{option?.label}</li>
          })}
        </ul>
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="calendarUnit"
          render={({ field: { onChange, onBlur, value } }) => (
            <Dropdown
              defaultValue={TRANSACTION_REPORT_CALENDAR_UNIT.find(
                (i) => i.value === String(value)
              )}
              title="Calendar Unit"
              options={TRANSACTION_REPORT_CALENDAR_UNIT}
              onSelect={(option: DropDownItem) => {
                onChange(option.value === '0' ? null : Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      <div className="flex gap-4 justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoading}
        >
          <span className="tracking-wider font-bold">
            {isLoading ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>

        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>

      {isError || (isSuccess && !report2Data?.data.length) ? (
        <div
          ref={resultRef}
          className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
        >
          <div>
            <p className="tracking-wide text-red font-light">
              {`Sorry, there are no items available.`}
            </p>
          </div>
        </div>
      ) : null}

      {isSuccess && report2Data?.data.length ? (() => {
        const csvKeys = Object.keys(Report2CSVDataSchemas.shape)
        const csvData = report2Data.data.map((row: Report2CSVDataTypes) =>
          Object.fromEntries(
            csvKeys.map((key) => [key, row[key as keyof Report2CSVDataTypes]])
          )
        )
        return (
          <div
            ref={resultRef}
            className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
          >
            <div>
              <p className="font-bold text-lg tracking-wide text-primary">
                Results:
              </p>
              <p className="tracking-wide text-primary font-light">
                The report is ready to view or download.
              </p>
            </div>

            <Button
              type="button"
              // icon={faDownload}
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={() =>
                ExportToCSV(csvData, { filename: 'Report-2' })
              }
            // disabled={transactionsLoading ? true : false}
            >
              Download CSV Report
            </Button>

            <Button
              type="button"
              // icon={faDownload}
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={handleView}
            >
              View Report
            </Button>
          </div>
        )
      })() : null}
    </form>
  )
}
